<template>
    <div class="mobile-app">
        <!-- Mobile Header (Positioned outside of scrollable content) -->
        <header class="mobile-header">
            <div class="header-content">
                <div class="profile-summary" v-if="data && data.name">
                    <div class="avatar">
                        <img :src="getImagePathWJ(data.logo)" :alt="data.name + ' avatar'">
                    </div>
                    <div class="profile-info">
                        <h1 class="name">{{ data.name }}</h1>
                        <p class="title">{{ data.title }}</p>
                    </div>
                </div>
                <div class="profile-summary" v-else>
                    <div class="profile-info">
                        <h1 class="name">Loading...</h1>
                    </div>
                </div>
                <button class="menu-toggle" @click="toggleMenu" :class="{ active: isMenuOpen }">
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>
        </header>

        <!-- Mobile Navigation Menu -->
        <nav class="mobile-nav" :class="{ open: isMenuOpen }">
            <div class="nav-overlay" @click="closeMenu"></div>
            <div class="nav-content">
                <div class="nav-header">
                    <h3>Navigation</h3>
                    <button class="close-btn" @click="closeMenu">
                        <ion-icon name="close"></ion-icon>
                    </button>
                </div>
                <ul class="nav-list">
                    <li v-for="item in (visibleNavItems || [])" :key="item.name" class="nav-item">
                        <button class="nav-link" 
                                :class="{ active: activeNav === item.name }"
                                @click="setActiveNav(item.name)">
                            <ion-icon :name="getNavIcon(item.name)"></ion-icon>
                            <span>{{ item.name }}</span>
                        </button>
                    </li>
                </ul>
                
                <!-- Contact Info in Mobile Menu -->
                <div class="mobile-contacts">
                    <h4>Get in Touch</h4>
                    <div class="contact-links">
                        <a v-if="data && data.email" :href="'mailto:' + data.email" class="contact-link">
                            <ion-icon name="mail"></ion-icon>
                            <span>Email</span>
                        </a>
                        <a v-if="data && data.linkedin" :href="data.linkedin" target="_blank" class="contact-link">
                            <ion-icon name="logo-linkedin"></ion-icon>
                            <span>LinkedIn</span>
                        </a>
                        <a v-if="data && data.github" :href="data.github" target="_blank" class="contact-link">
                            <ion-icon name="logo-github"></ion-icon>
                            <span>GitHub</span>
                        </a>
                    </div>
                    
                    <!-- Version Info -->
                    <div class="version-info">
                        <div class="version-item">
                            <ion-icon name="git-branch-outline"></ion-icon>
                            <span :title="version">{{ version }}</span>
                        </div>
                        <div class="version-item">
                            <ion-icon name="shield-checkmark-outline"></ion-icon>
                            <span>&copy; {{ new Date().getFullYear() }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </nav>

        <!-- Mobile Main Content -->
        <main class="mobile-main">
            <!-- Error component display -->
            <ErrorFallback v-if="hasError" :errorType="errorType" @go-home="resetError" />

            <!-- Mobile About Section -->
            <section v-else-if="activeNav === 'About' && data && data.name" class="mobile-section mobile-about">
                <!-- Hero Card -->
                <div class="hero-card">
                    <div class="hero-background"></div>
                    <div class="hero-content">
                        <div class="profile-image">
                            <img :src="getImagePathWJ(data.logo)" :alt="data.name">
                        </div>
                        <h2 class="greeting">Hello, I'm {{ data.name }}</h2>
                        <p class="profession">{{ data.title }}</p>
                        <div class="quick-stats">
                            <div class="stat">
                                <span class="number">7+</span>
                                <span class="label">Years</span>
                            </div>
                            <div class="stat">
                                <span class="number">{{ data.generalSkill ? data.generalSkill.length : 0 }}</span>
                                <span class="label">Skills</span>
                            </div>
                            <div class="stat">
                                <span class="number">{{ data.experience ? data.experience.length : 0 }}</span>
                                <span class="label">Companies</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- About Text Card -->
                <div class="content-card" v-if="data && data.about">
                    <h3 class="card-title">About Me</h3>
                    <p class="about-text">{{ data.about }}</p>
                </div>

                <!-- Skills Cards -->
                <div class="content-card" v-if="data && data.generalSkill">
                    <h3 class="card-title">What I Do</h3>
                    <div class="mobile-skills">
                        <div v-for="(service, index) in (data.generalSkill || [])" :key="index" 
                             class="skill-card">
                            <div class="skill-icon">
                                <img :src="getSkillIcon(service.type)" :alt="service.type">
                            </div>
                            <div class="skill-info">
                                <h4 class="skill-name">{{ service.type }}</h4>
                                <p class="skill-desc">{{ service.description }}</p>
                                <div class="skill-tags">
                                    <span v-for="tag in getSkillTags(service.type)" 
                                          :key="tag" 
                                          class="tag">
                                        {{ tag }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tech Stack -->
                <div class="content-card" v-if="techSkills && techSkills.length">
                    <h3 class="card-title">Tech Stack</h3>
                    <div class="tech-grid">
                        <div v-for="tech in (showAllTech ? (techSkills || []) : (techSkills || []).slice(0, 8))" :key="tech.title" 
                             class="tech-item">
                            <img :src="getImagePathTech(tech.imgSrc)" :alt="tech.title">
                            <span>{{ tech.title }}</span>
                        </div>
                    </div>
                    <button v-if="techSkills.length > 8" class="view-more-btn" @click="toggleTechView">
                        {{ showAllTech ? 'Show Less' : 'View More' }}
                    </button>
                </div>
            </section>

            <!-- Mobile Professional History -->
            <section v-else-if="activeNav === 'Professional History' && data && data.experience" class="mobile-section mobile-history">
                <div class="section-header">
                    <h2 class="section-title">Professional History</h2>
                </div>

                <!-- Experience Timeline -->
                <div class="content-card">
                    <h3 class="card-title">
                        <ion-icon name="briefcase"></ion-icon>
                        Experience
                    </h3>
                    <div class="timeline">
                        <div v-for="(exp, index) in (data.experience || [])" :key="index" 
                             class="timeline-item">
                            <div class="timeline-dot" :class="{ current: index === 0 }"></div>
                            <div class="timeline-content">
                                <div class="company-header">
                                    <img :src="getImagePathCompany(exp.logo)" :alt="exp.company" class="company-logo">
                                    <div class="company-info">
                                        <h4 class="company-name">{{ exp.company }}</h4>
                                        <p class="position">{{ exp.position }}</p>
                                        <span class="duration">{{ exp.duration }}</span>
                                    </div>
                                </div>
                                <div class="responsibilities">
                                    <button class="toggle-details" @click="toggleDetails(index)">
                                        <span>{{ (showDetails && showDetails[index]) ? 'Hide' : 'Show' }} Details</span>
                                        <ion-icon :name="(showDetails && showDetails[index]) ? 'chevron-up' : 'chevron-down'"></ion-icon>
                                    </button>
                                    <div v-if="showDetails && showDetails[index]" class="details-content">
                                        <ul class="responsibility-list">
                                            <li v-for="(resp, respIndex) in (exp.responsibilities || []).slice(0, 3)" 
                                                :key="respIndex">
                                                {{ resp }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Education -->
                <div class="content-card" v-if="data && data.education">
                    <h3 class="card-title">
                        <ion-icon name="school"></ion-icon>
                        Education
                    </h3>
                    <div class="education-list">
                        <div v-for="(edu, index) in (data.education || [])" :key="index" 
                             class="education-item">
                            <img :src="getImagePathCompany(edu.logo)" :alt="edu.place" class="institution-logo">
                            <div class="education-info">
                                <h4 class="institution">{{ edu.place }}</h4>
                                <p class="course">{{ edu.course }}</p>
                                <span class="duration">{{ edu.duration }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Other sections (Playground, SEO Test) -->
            <PlayGround v-else-if="activeNav === 'Playground'" 
                       :aboutText="data.about" 
                       :services="data.generalSkill"
                       :techSkills="techSkills" />
            <SeoTester v-else-if="activeNav === 'SEO Test'" />
        </main>

        <!-- Mobile Bottom Navigation (Alternative) -->
        <nav class="bottom-nav" v-if="false">
            <button v-for="item in (visibleNavItems || []).slice(0, 4)" 
                    :key="item.name" 
                    class="bottom-nav-item"
                    :class="{ active: activeNav === item.name }"
                    @click="setActiveNav(item.name)">
                <ion-icon :name="getNavIcon(item.name)"></ion-icon>
                <span>{{ item.name && item.name.split(' ').length > 0 ? item.name.split(' ')[0] : item.name }}</span>
            </button>
        </nav>
    </div>
</template>

<script>
import { getImagePathTech, getImagePathCompany, getImagePathWJ } from '@/utils/images';
import PlayGround from '../pages/PlayGround.vue';
import SeoTester from '../dev-tools/SeoTester.vue';
import ErrorFallback from '../common/ErrorFallback.vue';
import pkg from '../../../package.json';

export default {
    name: 'MobileView',
    components: {
        PlayGround,
        SeoTester,
        ErrorFallback
    },
    props: {
        data: Object,
        navItems: Array,
        techSkills: Array,
        activeNav: String,
        hasError: Boolean,
        errorType: String
    },
    data() {
        return {
            isMenuOpen: false,
            showAllTech: false,
            showAllSkills: false,
            hasDebugMode: false,
            showDetails: {}, // Track which experience details are expanded
            version: pkg.version
        };
    },
    computed: {
        visibleNavItems() {
            return (this.navItems || []).filter(item => item.name !== 'SEO Test');
        }
    },
    methods: {
        getImagePathTech,
        getImagePathCompany,
        getImagePathWJ,
        
        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen;
        },
        
        closeMenu() {
            this.isMenuOpen = false;
        },
        
        setActiveNav(nav) {
            this.$emit('nav-change', nav);
            this.closeMenu();
        },
        
        resetError() {
            this.$emit('reset-error');
        },
        
        toggleDetails(index) {
            // Direct toggle with proper reactivity
            this.showDetails[index] = !this.showDetails[index];
        },
        
        getNavIcon(navName) {
            const iconMap = {
                'About': 'person',
                'Professional History': 'briefcase',
                'Playground': 'code-slash',
                'SEO Test': 'analytics'
            };
            return iconMap[navName] || 'document';
        },
        
        getSkillIcon(type) {
            const iconMap = {
                'Web development': require('@/assets/images/tech/icons8-html-144.png'),
                'Mobile apps development': require('@/assets/images/tech/icons8-flutter-144.png'),
                'DevOps & Deployment': require('@/assets/images/tech/icons8-google-firebase-console-144.png'),
                'API and Service Integration': require('@/assets/images/tech/icons8-api-96.png')
            };
            return iconMap[type] || require('@/assets/icon-dev.svg');
        },
        
        getSkillTags(type) {
            const tagMap = {
                'Web development': ['Vue.js', 'Angular', 'TypeScript', 'HTML5'],
                'Mobile apps development': ['Flutter', 'Dart', 'iOS', 'Android'],
                'DevOps & Deployment': ['Firebase', 'Cloud Services', 'Server Setup', 'Performance Monitor'],
                'API and Service Integration': ['REST', 'Microservices', 'Spring Boot', 'Cloud']
            };
            return tagMap[type] || ['Technology'];
        },
        
        toggleTechView() {
            // Directly toggle tech view for better reliability
            this.showAllTech = !this.showAllTech;
        }
    }
};
</script>

<style scoped>
/* Simple reset styles */
:global(body), :global(html) {
    overflow: visible;
    height: 100%;
}

.mobile-app {
    min-height: 100vh;
    background: var(--eerie-black-1);
    color: var(--white-1);
    position: relative;
    overflow-x: hidden;
    /* Enable native scrolling */
    -webkit-overflow-scrolling: touch;
}

/* Mobile Header */
.mobile-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: rgba(23, 23, 23, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    z-index: 100; /* Lower z-index to prevent blocking */
    padding: 1rem;
    pointer-events: auto; /* Allow interaction with header */
    /* Ensure header doesn't interfere with scroll */
    will-change: transform;
    transform: translate3d(0, 0, 0);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.profile-summary {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #667eea;
}

.avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.profile-info .name {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: var(--white-1);
}

.profile-info .title {
    font-size: 0.85rem;
    color: #667eea;
    margin: 0;
}

/* Hamburger Menu */
.menu-toggle {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

.menu-toggle span {
    display: block;
    height: 3px;
    width: 100%;
    background: var(--white-1);
    margin: 3px 0;
    transition: 0.3s;
    border-radius: 2px;
}

.menu-toggle.active span:nth-child(1) {
    transform: rotate(-45deg) translate(-6px, 6px);
}

.menu-toggle.active span:nth-child(2) {
    opacity: 0;
}

.menu-toggle.active span:nth-child(3) {
    transform: rotate(45deg) translate(-6px, -6px);
}

/* Mobile Navigation */
.mobile-nav {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1001;
    visibility: hidden;
    opacity: 0;
    transition: all 0.3s ease;
    pointer-events: none; /* Prevent interaction when closed */
}

.mobile-nav.open {
    visibility: visible;
    opacity: 1;
    pointer-events: auto; /* Enable interaction when open */
}

.nav-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
}

.nav-content {
    position: absolute;
    top: 0;
    right: 0;
    width: 80%;
    max-width: 300px;
    height: 100%;
    background: var(--eerie-black-2);
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    transform: translateX(100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
}

.mobile-nav.open .nav-content {
    transform: translateX(0);
}

.nav-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-header h3 {
    margin: 0;
    color: var(--white-1);
}

.close-btn {
    background: none;
    border: none;
    color: var(--white-1);
    font-size: 1.5rem;
    cursor: pointer;
}

.nav-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.nav-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.nav-link {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    padding: 1rem;
    background: none;
    border: none;
    color: var(--light-gray-70);
    text-align: left;
    transition: all 0.3s ease;
    cursor: pointer;
}

.nav-link:hover, .nav-link.active {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
}

.nav-link ion-icon {
    font-size: 1.2rem;
}

/* Mobile Contacts */
.mobile-contacts {
    padding: 1.5rem 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin-top: auto;
}

.mobile-contacts h4 {
    margin: 0 0 1rem 0;
    color: var(--white-1);
    font-size: 0.9rem;
}

.contact-links {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.contact-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--light-gray-70);
    text-decoration: none;
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.contact-link:hover {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
}

/* Version Info */
.version-info {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.version-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0;
    font-size: 0.8rem;
    color: var(--light-gray-70);
}

.version-item ion-icon {
    font-size: 0.9rem;
    color: #667eea;
}

/* Mobile Main Content */
.mobile-main {
    margin-top: 80px;
    padding: 1rem;
    min-height: calc(100vh - 80px);
    position: relative;
    z-index: 1; /* Ensure content is above any potential hidden elements */
    /* Enable smooth scrolling */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* Allow content to expand beyond viewport */
    height: auto;
}

.mobile-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Hero Card */
.hero-card {
    position: relative;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 2rem 1.5rem;
    text-align: center;
    overflow: hidden;
}

.hero-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
    pointer-events: none;
}

.hero-content {
    position: relative;
    z-index: 1;
}

.profile-image {
    width: 80px;
    height: 80px;
    margin: 0 auto 1rem;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid #667eea;
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.profile-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.greeting {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.profession {
    color: var(--light-gray-70);
    font-size: 1.1rem;
    margin: 0 0 1.5rem 0;
}

.quick-stats {
    display: flex;
    justify-content: space-around;
    gap: 1rem;
}

.stat {
    text-align: center;
}

.stat .number {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    color: #667eea;
}

.stat .label {
    font-size: 0.8rem;
    color: var(--light-gray-70);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Content Cards */
.content-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 1.5rem;
}

.card-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
    color: var(--white-1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.about-text {
    color: var(--light-gray-70);
    line-height: 1.6;
    margin: 0;
}

/* Mobile Skills */
.mobile-skills {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.skill-card {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    transition: all 0.3s ease;
}

.skill-card:hover {
    background: rgba(102, 126, 234, 0.1);
    border-color: rgba(102, 126, 234, 0.2);
}

.skill-icon {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(102, 126, 234, 0.1);
}

.skill-icon img {
    width: 30px;
    height: 30px;
    object-fit: contain;
}

.skill-info {
    flex: 1;
}

.skill-name {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    color: var(--white-1);
}

.skill-desc {
    font-size: 0.85rem;
    color: var(--light-gray-70);
    line-height: 1.4;
    margin: 0 0 0.75rem 0;
}

.skill-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.tag {
    background: rgba(102, 126, 234, 0.2);
    color: var(--white-2);
    padding: 0.2rem 0.5rem;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 500;
}

/* Tech Grid */
.tech-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;
}

.tech-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 10px;
    transition: all 0.3s ease;
}

.tech-item:hover {
    background: rgba(102, 126, 234, 0.1);
    transform: translateY(-2px);
}

.tech-item img {
    width: 30px;
    height: 30px;
    object-fit: contain;
}

.tech-item span {
    font-size: 0.7rem;
    color: var(--light-gray-70);
    text-align: center;
}

.view-more-btn {
    width: 100%;
    padding: 0.75rem;
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 10px;
    color: #667eea;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.view-more-btn:hover, 
.view-more-btn:active {
    background: rgba(102, 126, 234, 0.2);
}

/* Timeline */
.timeline {
    position: relative;
}

.timeline-item {
    position: relative;
    padding-left: 2rem;
    margin-bottom: 1.5rem;
}

.timeline-item:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 9px;
    top: 30px;
    width: 2px;
    height: calc(100% + 0.5rem);
    background: linear-gradient(180deg, #667eea 0%, rgba(102, 126, 234, 0.3) 100%);
}

.timeline-dot {
    position: absolute;
    left: 0;
    top: 10px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--eerie-black-1);
    border: 3px solid #667eea;
    z-index: 1;
}

.timeline-dot.current {
    background: #667eea;
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
}

.timeline-content {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 1rem;
}

.company-header {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.company-logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
}

.company-info {
    flex: 1;
}

.company-name {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.25rem 0;
    color: var(--white-1);
}

.position {
    font-size: 0.9rem;
    color: #667eea;
    margin: 0 0 0.25rem 0;
}

.duration {
    font-size: 0.8rem;
    color: var(--light-gray-70);
}

.toggle-details {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.5rem;
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 8px;
    color: #667eea;
    cursor: pointer;
    font-size: 0.9rem;
}

.details-content {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.responsibility-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.responsibility-list li {
    position: relative;
    padding-left: 1rem;
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    color: var(--light-gray-70);
    line-height: 1.4;
}

.responsibility-list li::before {
    content: '▸';
    position: absolute;
    left: 0;
    color: #667eea;
}

/* Education List */
.education-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.education-item {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
}

.institution-logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
}

.education-info {
    flex: 1;
}

.institution {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.25rem 0;
    color: var(--white-1);
}

.course {
    font-size: 0.9rem;
    color: #667eea;
    margin: 0 0 0.25rem 0;
}

.section-header {
    text-align: center;
    margin-bottom: 1.5rem;
}

.section-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Bottom Navigation (Optional) */
.bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(23, 23, 23, 0.95);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-around;
    padding: 0.5rem;
    z-index: 1000;
}

.bottom-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem;
    background: none;
    border: none;
    color: var(--light-gray-70);
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px;
    flex: 1;
}

.bottom-nav-item.active {
    color: #667eea;
    background: rgba(102, 126, 234, 0.1);
}

.bottom-nav-item ion-icon {
    font-size: 1.2rem;
}

.bottom-nav-item span {
    font-size: 0.7rem;
}

/* Responsive adjustments */
@media (max-width: 375px) {
    .mobile-main {
        padding: 0.75rem;
    }
    
    .content-card {
        padding: 1rem;
    }
    
    .hero-card {
        padding: 1.5rem 1rem;
    }
    
    .tech-grid {
        grid-template-columns: repeat(3, 1fr);
    }
    
    /* Mobile copyright responsive */
    .copyright-item {
        padding: 0.5rem;
        font-size: 0.8rem;
    }
    
    .copyright-item ion-icon {
        font-size: 1rem;
    }
}

/* Additional mobile scroll fixes */
@media (max-width: 768px) {
    /* Ensure the mobile app can scroll */
    .mobile-app {
        height: auto !important;
        min-height: 100vh;
        position: relative;
        /* Allow all touch actions on mobile */
        touch-action: pan-y pinch-zoom;
    }
    
    /* Fix for iOS safari scroll issues */
    .mobile-main {
        -webkit-transform: translate3d(0,0,0);
        transform: translate3d(0,0,0);
        /* Remove max-height on very small screens */
        max-height: none;
        /* Allow vertical panning (scrolling) */
        touch-action: pan-y;
    }
    
    /* Ensure sections don't constrain height */
    .mobile-section {
        height: auto;
        min-height: auto;
        /* Allow touch scrolling on sections */
        touch-action: pan-y;
    }
    
    /* Fix for content cards */
    .content-card, .hero-card {
        position: relative;
        height: auto;
        /* Allow touch actions on cards */
        touch-action: pan-y;
    }
}

/* iOS specific fixes */
@supports (-webkit-touch-callout: none) {
    .mobile-main {
        /* Fix for iOS momentum scrolling */
        -webkit-overflow-scrolling: touch;
        /* Prevent scroll bounce */
        overscroll-behavior: contain;
    }
    
    body {
        /* Prevent zoom on input focus */
        -webkit-text-size-adjust: 100%;
    }
}
</style>
